// ─── src/hooks/useNpd.ts ─────────────────────────────────────────────────────

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  deleteNpdNomination,
  deleteNpdRegistration,
  deleteNpdRsvp,
  getNpdNominations,
  getNpdNominationStats,
  getNpdRegistrations,
  getNpdRegistrationStats,
  getNpdRsvps,
  getNpdRsvpStats,
  reviewNpdRegistration,
  type NpdNominationParams,
  type NpdRegistrationParams,
  type NpdRegistrationReviewPatch,
  type NpdRsvpParams,
} from "../api/npd";

// ── RSVPs ────────────────────────────────────────────────────────────────────

export const npdRsvpKeys = {
  all:   ["npdRsvps"] as const,
  list:  (p: NpdRsvpParams) => [...npdRsvpKeys.all, "list", p] as const,
  stats: () => [...npdRsvpKeys.all, "stats"] as const,
};

export const useNpdRsvps = (params: NpdRsvpParams) =>
  useQuery({
    queryKey: npdRsvpKeys.list(params),
    queryFn:  () => getNpdRsvps(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

export const useNpdRsvpStats = () =>
  useQuery({
    queryKey: npdRsvpKeys.stats(),
    queryFn: getNpdRsvpStats,
    staleTime: 60_000,
  });

export const useDeleteNpdRsvp = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteNpdRsvp,
    onSuccess: () => qc.invalidateQueries({ queryKey: npdRsvpKeys.all }),
  });
};

// ── Nominations (waitlist) ───────────────────────────────────────────────────

export const npdNominationKeys = {
  all:   ["npdNominations"] as const,
  list:  (p: NpdNominationParams) => [...npdNominationKeys.all, "list", p] as const,
  stats: () => [...npdNominationKeys.all, "stats"] as const,
};

export const useNpdNominations = (params: NpdNominationParams) =>
  useQuery({
    queryKey: npdNominationKeys.list(params),
    queryFn:  () => getNpdNominations(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

export const useNpdNominationStats = () =>
  useQuery({
    queryKey: npdNominationKeys.stats(),
    queryFn: getNpdNominationStats,
    staleTime: 60_000,
  });

export const useDeleteNpdNomination = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteNpdNomination,
    onSuccess: () => qc.invalidateQueries({ queryKey: npdNominationKeys.all }),
  });
};

// ── Full registrations (founder application form) ────────────────────────────

export const npdRegistrationKeys = {
  all: ["npdRegistrations"] as const,
  list: (p: NpdRegistrationParams) =>
    [...npdRegistrationKeys.all, "list", p] as const,
  stats: () => [...npdRegistrationKeys.all, "stats"] as const,
};

export const useNpdRegistrations = (params: NpdRegistrationParams) =>
  useQuery({
    queryKey: npdRegistrationKeys.list(params),
    queryFn: () => getNpdRegistrations(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

export const useNpdRegistrationStats = () =>
  useQuery({
    queryKey: npdRegistrationKeys.stats(),
    queryFn: getNpdRegistrationStats,
    staleTime: 60_000,
  });

export const useDeleteNpdRegistration = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteNpdRegistration,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: npdRegistrationKeys.all }),
  });
};

export const useReviewNpdRegistration = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: NpdRegistrationReviewPatch;
    }) => reviewNpdRegistration(id, patch),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: npdRegistrationKeys.all }),
  });
};
